"use client";

import {
  ArrowRight,
  Eye,
  Flame,
  Inbox,
  type LucideIcon,
  Plus,
  Sparkles,
  Zap,
} from "lucide-react";
import Link from "next/link";

import { Container } from "@/app/(marketing)/_components/ui/container";
import {
  type AdvertisedFigure,
  type AdvertisedRates,
  AdvertisedRatesProvider,
} from "@/components/opportunities/advertised-pay";
import {
  OpportunityCard,
  type OpportunityListingCard,
} from "@/components/opportunities/opportunity-card";

import { getAudienceByToken } from "../[audience]/_config/audiences";

const STATUS_META: {
  label: string;
  icon: LucideIcon;
  className: string;
}[] = [
  { label: "High demand", icon: Flame, className: "text-destructive" },
  { label: "Active matching", icon: Zap, className: "text-emerald-600" },
  { label: "Recently added", icon: Sparkles, className: "text-brand-blue" },
  { label: "New openings", icon: Plus, className: "text-brand-blue" },
  { label: "Expert review", icon: Eye, className: "text-brand-blue" },
  { label: "Ongoing intake", icon: Inbox, className: "text-brand-blue" },
];

function mulberry32(seed: number) {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Deterministic per-day shuffle so every visitor sees the same assignment for
// the day and the rotation flips at 12:01 AM UTC. UTC keeps SSR and client
// hydration consistent regardless of where the visitor is.
function getDailyStatuses(
  nowMs: number = Date.now(),
): (typeof STATUS_META)[number][] {
  const dayIndex = Math.floor((nowMs - 60 * 1000) / 86_400_000);
  const rand = mulberry32(dayIndex);
  const arr = [...STATUS_META];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}

const opportunities: Omit<OpportunityListingCard, "referralsEnabled">[] = [
  {
    id: "finance",
    token: "finance",
    studyId: null,
    title: "Finance",
    shortDescription: null,
    tags: null,
    payRateMin: "60",
    payRateMax: "160",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "FP&A, Treasury, Tax, Audit, or Credit Risk Analyst (US, 5–10 yrs)",
      "AP/AR, Corp Dev, Banking, Compliance, or Ops Analyst",
    ],
    referralTitle: "Finance",
    spotsRemaining: 48,
  },
  {
    id: "accounting",
    token: "accounting",
    studyId: null,
    title: "Accounting",
    shortDescription: null,
    tags: null,
    payRateMin: "50",
    payRateMax: "140",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "Staff, Senior, Tax, Cost, or Grant Accountant (US, 5–10 yrs)",
      "Audit Associate, EBP Senior, CAS, SOX / Internal Audit",
    ],
    referralTitle: "Accounting",
    spotsRemaining: 62,
  },
  {
    id: "healthcare-ops",
    token: "healthcare-ops",
    studyId: null,
    title: "Healthcare Ops",
    shortDescription: null,
    tags: null,
    payRateMin: "50",
    payRateMax: "120",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "Medical Coder, CDI Specialist, or Revenue Cycle Analyst (US, 5–10 yrs)",
      "UM Nurse, Case Manager, Quality, Compliance, or Risk Coordinator",
    ],
    referralTitle: "Healthcare Ops",
    spotsRemaining: 71,
  },
  {
    id: "swe",
    token: "swe",
    studyId: null,
    title: "Software Engineering",
    shortDescription: null,
    tags: null,
    payRateMin: "60",
    payRateMax: "200",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "Full Stack, Backend, Frontend, Mobile, or ML Engineer (US/UK/CA, 2+ yrs)",
      "DevOps / Platform, SRE, or Data Engineer",
    ],
    referralTitle: "Software Engineering",
    spotsRemaining: 134,
  },
  {
    id: "consultants",
    token: "consultants",
    studyId: null,
    title: "Management Consultants",
    shortDescription: null,
    tags: null,
    payRateMin: "90",
    payRateMax: "200",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "5+ years at a top strategy or ops consulting firm",
      "Experience advising Fortune 500 leadership",
    ],
    referralTitle: "Management Consultants",
    spotsRemaining: 104,
  },
  {
    id: "platform-experts",
    token: "platform-experts",
    studyId: null,
    title: "Product / Platform Experts",
    shortDescription: null,
    tags: null,
    payRateMin: "70",
    payRateMax: "160",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "Senior PM or platform engineer at a top tech company",
      "Shipped infra, dev tools, or developer-facing products",
    ],
    referralTitle: "Product / Platform Experts",
    spotsRemaining: 120,
  },
  {
    id: "education",
    token: "education",
    studyId: null,
    title: "Education / Student",
    shortDescription: null,
    tags: null,
    payRateMin: "70",
    payRateMax: "140",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "Currently enrolled in a top-tier university",
      "STEM, humanities, or pre-professional track",
    ],
    referralTitle: "Education / Student",
    spotsRemaining: 75,
  },
  {
    id: "operations",
    token: "operations",
    studyId: null,
    title: "Operations / Program Management",
    shortDescription: null,
    tags: null,
    payRateMin: "70",
    payRateMax: "140",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "Led cross-functional programs at scale",
      "Operations, supply chain, or program management experience",
    ],
    referralTitle: "Operations / Program Management",
    spotsRemaining: 33,
  },
  {
    id: "legal",
    token: "legal",
    studyId: null,
    title: "Lawyers / Legal Specialists",
    shortDescription: null,
    tags: null,
    payRateMin: "100",
    payRateMax: "200",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "JD with active bar admission",
      "Practice at a top firm or in-house counsel role",
    ],
    referralTitle: "Lawyers / Legal Specialists",
    spotsRemaining: 87,
  },
  {
    id: "pharmacy",
    token: "pharmacy",
    studyId: null,
    title: "Pharmacists",
    shortDescription: null,
    tags: null,
    payRateMin: "90",
    payRateMax: "150",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "PharmD with active license",
      "Clinical, retail, or hospital pharmacy practice",
    ],
    referralTitle: "Pharmacists",
    spotsRemaining: 44,
  },
  {
    id: "data",
    token: "data",
    studyId: null,
    title: "Data / Analytics Experts",
    shortDescription: null,
    tags: null,
    payRateMin: "80",
    payRateMax: "160",
    payRateFrequency: "HOURLY",
    workMode: "REMOTE",
    opportunityType: "INTERVIEW",
    referralBonusAmount: null,
    referralCommissionBps: 0,
    publishedAt: null,
    requirements: [
      "Senior data scientist or analytics engineer",
      "Production ML, experimentation, or BI ownership",
    ],
    referralTitle: "Data / Analytics Experts",
    spotsRemaining: 50,
  },
];

/**
 * These cards are hiring categories, not studies, so no rate read answers for
 * them. Their illustrative top-of-range figures reach the card the way a real
 * study's resolved figure does, keyed by the category id in `studyId`.
 */
const CATEGORY_RATES: AdvertisedRates = {
  byStudy: new Map(
    opportunities.flatMap((opp): [string, AdvertisedFigure][] =>
      opp.payRateMax && isRateUnit(opp.payRateFrequency)
        ? [
            [
              opp.id,
              {
                rateUsd: opp.payRateMax,
                payRateFrequency: opp.payRateFrequency,
              },
            ],
          ]
        : [],
    ),
  ),
  pending: false,
};

function isRateUnit(
  value: string,
): value is AdvertisedFigure["payRateFrequency"] {
  return value === "ONE_TIME" || value === "HOURLY" || value === "PER_TASK";
}

export default function ExpertsLatestOpportunities() {
  const visible = opportunities.slice(0, 6);
  const statuses = getDailyStatuses();

  return (
    <section className="relative z-10 -mt-32 pb-12 md:-mt-44">
      <Container>
        <div className="grid grid-cols-1 gap-4 px-1 py-2 sm:grid-cols-2 sm:gap-[14px] lg:grid-cols-3">
          {visible.map((opp, i) => {
            const audience = getAudienceByToken(opp.token);
            const href = audience ? `/experts/${audience.slug}` : "/login";
            const status = statuses[i]!;
            const cardData = {
              ...opp,
              studyId: opp.id,
              // A hiring category, not a study: no organization's setting
              // reaches it.
              referralsEnabled: true,
              statusLabel: status.label,
              statusIcon: status.icon,
              statusClassName: status.className,
            };
            return (
              <Link key={opp.id} href={href} className="block no-underline">
                <AdvertisedRatesProvider rates={CATEGORY_RATES}>
                  <OpportunityCard
                    opportunity={cardData}
                    variant="referral"
                    onSelect={() => {
                      /* navigation handled by wrapping Link */
                    }}
                    className="shadow-[0_4px_20px_rgba(0,6,183,0.10),_0_2px_6px_rgba(0,6,183,0.06)]"
                  />
                </AdvertisedRatesProvider>
              </Link>
            );
          })}
        </div>

        <div className="mt-8 flex justify-center">
          <Link
            href="/login"
            className="text-marketing-text group inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-6 py-3 text-sm font-medium shadow-[0_2px_8px_rgba(0,6,183,0.08)] transition-colors hover:bg-white/90"
          >
            See all open opportunities
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </Container>
    </section>
  );
}
